import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import { api } from 'common';
import { initializeChatUser } from 'common/sharedFunctions';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import EuroIcon from '@mui/icons-material/EuroSymbol';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

// Brand tokens — match landing palette / typography exactly
const BRAND = {
    teal: '#4fd1c5',
    tealDark: '#2dd4bf',
    tealLight: 'rgba(79,209,197,0.12)',
    green: '#10b981',
    greenLight: 'rgba(16,185,129,0.12)',
    indigo: '#6366f1',
    indigoLight: 'rgba(99,102,241,0.12)',
    rose: '#f43f5e',
    slate950: '#020617',
    slate900: '#0f172a',
    slate800: '#1e293b',
    slate700: '#334155',
    slate500: '#64748b',
    slate400: '#94a3b8',
    slate300: '#cbd5e1',
    slate200: '#e2e8f0',
    slate100: '#f1f5f9',
    slate50: '#f8fafc',
    white: '#ffffff',
    font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
};

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);

    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const navigate = useNavigate();
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const { fetchDrivers, clearFetchDrivers } = api;

    // Inject Inter font (matches landing). Once per mount.
    useEffect(() => {
        if (document.querySelector('link[data-medlift-inter]')) return;
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap';
        link.rel = 'stylesheet';
        link.setAttribute('data-medlift-inter', '1');
        document.head.appendChild(link);
    }, []);

    useEffect(() => {
        initializeChatUser(auth?.profile);
    }, [auth]);

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation) return;
        // ponytail: without a fallback the map stayed on "Caricamento mappa..." forever when the browser denied geolocation
        const fallback = () => setMylocation(
            locations.length ? { lat: locations[0].lat, lng: locations[0].lng } : { lat: 46.1951, lng: 9.0233 } // Bellinzona
        );
        if (!navigator.geolocation) return fallback();
        navigator.geolocation.getCurrentPosition(
            position => setMylocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude
            }),
            fallback,
            { timeout: 5000 }
        );
    }, [mylocation, locations]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];
            for (let i = 0; i < drivers.length; i++) {
                if (drivers[i].location) {
                    let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                    let bookingRef = null;
                    for (let j = 0; j < cars.length; j++) {
                        if (cars[j].name === drivers[i].carType) {
                            carImage = cars[j].image;
                        }
                    }
                    for (let j = 0; j < liveBookings.length; j++) {
                        if (liveBookings[j].driver === drivers[i].uid) {
                            bookingRef = liveBookings[j].reference;
                        }
                    }
                    locs.push({
                        id: i,
                        lat: drivers[i].location.lat,
                        lng: drivers[i].location.lng,
                        drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                        carnumber: drivers[i].vehicleNumber,
                        cartype: drivers[i].carType,
                        carImage: carImage,
                        bookingRef: bookingRef,
                        fleetadmin: drivers[i].fleetadmin
                    });
                }
            }
            if (auth.profile.usertype === 'fleetadmin') {
                locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
            }
            setLocations(locs);
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);

    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin') || auth.profile.usertype === 'admin')) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings, auth.profile.uid, auth.profile.usertype]);

    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin' || auth.profile.usertype === 'customer') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin' || auth.profile.usertype === 'customer') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin' || auth.profile.usertype === 'customer') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0);
            setDriverCount(0);
            setCustomerCount(0);
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const totalBuckets = new Array(12).fill(0);
        const cancelBuckets = new Array(12).fill(0);
        if (bookinglistdata.bookings) {
            for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                const b = bookinglistdata.bookings[j];
                // Use bookingDate (creation) as primary so newly created bookings appear immediately;
                // fall back to tripdate for legacy records that lack bookingDate.
                const ref = b.bookingDate || b.tripdate;
                if (!ref) continue;
                const tDate = new Date(ref);
                if (isNaN(tDate.getTime()) || tDate.getFullYear() !== currentYear) continue;
                const m = tDate.getMonth();
                if (b.status === 'CANCELLED') {
                    cancelBuckets[m] += 1;
                } else {
                    // Every non-cancelled booking counts toward the total (NEW, ACCEPTED, STARTED, COMPLETE, etc.)
                    totalBuckets[m] += 1;
                }
            }
        }
        setAllCompleteCount(totalBuckets);
        setAllCancelCount(cancelBuckets);
    }, [bookinglistdata.bookings]);

    // ───────────────── chart options redesigned ─────────────────
    const chartShortMonths = [
        t('jan') || 'Gen', t('feb') || 'Feb', t('mar') || 'Mar', t('apr') || 'Apr',
        t('may') || 'Mag', t('jun') || 'Giu', t('jul') || 'Lug', t('aug') || 'Ago',
        t('sep') || 'Set', t('oct') || 'Ott', t('nov') || 'Nov', t('dec') || 'Dic',
    ];

    const chartOptions = {
        chart: {
            id: 'bookings-area',
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: BRAND.font,
        },
        colors: [BRAND.teal, BRAND.rose],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5 },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [0, 90, 100],
            },
        },
        grid: {
            borderColor: BRAND.slate200,
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
        },
        xaxis: {
            categories: chartShortMonths,
            labels: { style: { fontFamily: BRAND.font, colors: BRAND.slate500, fontSize: '12px' } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: { style: { fontFamily: BRAND.font, colors: BRAND.slate500, fontSize: '12px' } },
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontFamily: BRAND.font,
            fontSize: '13px',
            fontWeight: 600,
            labels: { colors: BRAND.slate700 },
            markers: { width: 8, height: 8, radius: 4 },
            itemMargin: { horizontal: 12 },
        },
        tooltip: {
            theme: 'light',
            style: { fontFamily: BRAND.font },
        },
    };

    const chartSeries = [
        { name: 'Prenotazioni', data: allCompleteCount },
        { name: 'Annullate', data: allCancelCount },
    ];

    const greeting = (() => {
        const h = new Date().getHours();
        if (h < 12) return 'Buongiorno';
        if (h < 18) return 'Buon pomeriggio';
        return 'Buonasera';
    })();

    const roleLabel = ({
        admin: 'Amministratore',
        fleetadmin: 'Fleet Admin',
        customer: 'Struttura',
        driver: 'Trasportatore',
    })[auth?.profile?.usertype] || '';

    const symbolBefore = settings.swipe_symbol === false;
    const fmtCurrency = (v) => symbolBefore ? `${settings.symbol || ''} ${v}` : `${v} ${settings.symbol || ''}`;

    // ───────────────── render ─────────────────
    if (bookinglistdata.loading || usersdata.loading) return <CircularLoading />;

    return (
        <Box
            dir={isRTL === 'rtl' ? 'rtl' : 'ltr'}
            sx={{
                fontFamily: BRAND.font,
                p: { xs: 2, sm: 3, md: 4 },
                pb: { xs: 4, md: 6 },
                backgroundColor: BRAND.slate50,
                minHeight: 'calc(100vh - 64px)',
            }}
        >
            <style>{KEYFRAMES_CSS}</style>

            {/* Welcome strip */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: { xs: 3, md: 4 },
                    animation: 'ml-fade-up 500ms cubic-bezier(0.16,1,0.3,1) both',
                }}
            >
                <Box>
                    {roleLabel && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.4,
                                borderRadius: '999px',
                                backgroundColor: BRAND.tealLight,
                                border: `1px solid rgba(79,209,197,0.3)`,
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: BRAND.teal,
                                    animation: 'ml-pulse 2s ease-in-out infinite',
                                }}
                            />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND.teal, fontFamily: BRAND.font, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                {roleLabel}
                            </Typography>
                        </Box>
                    )}
                    <Typography
                        sx={{
                            fontSize: { xs: 22, sm: 26, md: 30 },
                            fontWeight: 700,
                            color: BRAND.slate900,
                            fontFamily: BRAND.font,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.15,
                        }}
                    >
                        {greeting}{auth?.profile?.firstName ? `, ${auth.profile.firstName}` : ''} 👋
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: 13, md: 14 },
                            color: BRAND.slate500,
                            fontFamily: BRAND.font,
                            mt: 0.5,
                        }}
                    >
                        Ecco il riepilogo della tua attività MedLift
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '12px',
                        backgroundColor: BRAND.white,
                        border: `1px solid ${BRAND.slate200}`,
                        fontSize: 13,
                        color: BRAND.slate700,
                        fontWeight: 500,
                    }}
                >
                    <CalendarTodayIcon sx={{ fontSize: 16, color: BRAND.slate500 }} />
                    {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                </Box>
            </Box>

            {/* KPI ROW — hidden for customer (Struttura) since earnings don't apply */}
            {auth?.profile?.usertype !== 'customer' && (
                <>
                    <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>{t('gross_earning') || 'Entrate'}</Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                            gap: { xs: 2, md: 2.5 },
                            mb: { xs: 3, md: 4 },
                        }}
                    >
                        <KpiCard
                            delay={0}
                            accent={BRAND.teal}
                            accentLight={BRAND.tealLight}
                            icon={<EuroIcon sx={{ fontSize: 18, color: BRAND.teal }} />}
                            label={t('today_text') || 'Oggi'}
                            value={fmtCurrency(dailygross || 0)}
                            helper="Entrate giornaliere"
                        />
                        <KpiCard
                            delay={80}
                            accent={BRAND.indigo}
                            accentLight={BRAND.indigoLight}
                            icon={<CalendarTodayIcon sx={{ fontSize: 18, color: BRAND.indigo }} />}
                            label={t('this_month_text') || 'Questo mese'}
                            value={fmtCurrency(monthlygross || 0)}
                            helper="Entrate mese in corso"
                        />
                        <KpiCard
                            delay={160}
                            accent={BRAND.green}
                            accentLight={BRAND.greenLight}
                            icon={<TrendingUpIcon sx={{ fontSize: 18, color: BRAND.green }} />}
                            label={t('total') || 'Totale'}
                            value={fmtCurrency(totalgross || 0)}
                            helper="Entrate cumulative"
                        />
                    </Box>
                </>
            )}

            {/* CUSTOMER ONLY — Payouts to drivers widget */}
            {auth?.profile?.usertype === 'customer' && (() => {
                const myUid = auth?.profile?.uid;
                const all = bookinglistdata?.bookings || [];
                let pendingTotal = 0;
                let paidTotal = 0;
                const pendingDrivers = new Set();
                let pendingTrips = 0;
                all.forEach(b => {
                    if (b.customer !== myUid) return;
                    if (b.status !== 'COMPLETE' && b.status !== 'PAID') return;
                    if (b.paidBy !== 'Struttura' && b.paidBy !== 'Facility') return;
                    const cost = Number(b.trip_cost || 0);
                    if (b.facilityPayout === 'PAID') {
                        paidTotal += cost;
                    } else {
                        pendingTotal += cost;
                        pendingTrips += 1;
                        if (b.driver) pendingDrivers.add(b.driver);
                    }
                });
                const sym = settings?.symbol || 'CHF';
                const fmt = (n) => settings?.swipe_symbol === false ? `${sym} ${Number(n).toFixed(2)}` : `${Number(n).toFixed(2)} ${sym}`;
                if (pendingTotal === 0 && paidTotal === 0) return null;
                return (
                    <Box
                        onClick={() => navigate('/payouts')}
                        sx={{
                            mb: { xs: 3, md: 4 },
                            backgroundColor: '#fff',
                            border: `1px solid ${BRAND.slate200}`,
                            borderRadius: '16px',
                            p: { xs: 2, md: 2.5 },
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            '&:hover': {
                                borderColor: pendingTotal > 0 ? '#f59e0b' : BRAND.teal,
                                boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
                            },
                        }}
                    >
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            backgroundColor: pendingTotal > 0 ? 'rgba(245,158,11,0.10)' : 'rgba(16,185,129,0.10)',
                            color: pendingTotal > 0 ? '#f59e0b' : '#10b981',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <EuroIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, fontWeight: 700, color: BRAND.slate500, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.25 }}>
                                Pagamenti Trasportatori
                            </Typography>
                            <Typography sx={{ fontFamily: BRAND.font, fontSize: 18, fontWeight: 800, color: BRAND.slate900, letterSpacing: '-0.02em' }}>
                                {pendingTotal > 0
                                    ? `Devi ${fmt(pendingTotal)} a ${pendingDrivers.size} ${pendingDrivers.size === 1 ? 'trasportatore' : 'trasportatori'}`
                                    : `Tutto saldato — ${fmt(paidTotal)} pagati`
                                }
                            </Typography>
                            {pendingTotal > 0 && (
                                <Typography sx={{ fontFamily: BRAND.font, fontSize: 12, color: BRAND.slate500, mt: 0.25 }}>
                                    {pendingTrips} {pendingTrips === 1 ? 'trasporto in attesa' : 'trasporti in attesa'} di pagamento
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{
                            fontFamily: BRAND.font,
                            fontSize: 13, fontWeight: 700,
                            color: BRAND.tealDark,
                            display: { xs: 'none', sm: 'block' },
                        }}>
                            Vedi dettagli →
                        </Box>
                    </Box>
                );
            })()}

            {/* MAP + SIDEBAR STATS */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                    gap: { xs: 2, md: 2.5 },
                    mb: { xs: 3, md: 4 },
                }}
            >
                {/* Map card */}
                <SurfaceCard delay={240} sx={{ p: 0, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: { xs: 2, md: 3 },
                            py: 2,
                            borderBottom: `1px solid ${BRAND.slate200}`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Box sx={iconBoxSx(BRAND.tealLight)}>
                                <RoomOutlinedIcon sx={{ fontSize: 18, color: BRAND.teal }} />
                            </Box>
                            <Box>
                                <Typography sx={cardTitleSx}>{t('real_time_driver_section_text') || 'Trasportatori live'}</Typography>
                                <Typography sx={cardSubtitleSx}>{locations.length} {locations.length === 1 ? 'attivo' : 'attivi'}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: BRAND.green,
                                    animation: 'ml-pulse 2s ease-in-out infinite',
                                }}
                            />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND.green, fontFamily: BRAND.font, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                Live
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ height: { xs: 280, md: 420 } }}>
                        {mylocation && mylocation.lat ? (
                            <GoogleMap
                                zoom={10}
                                center={mylocation}
                                mapContainerStyle={{ height: '100%', width: '100%', fontFamily: BRAND.font }}
                                options={{
                                    disableDefaultUI: false,
                                    streetViewControl: false,
                                    mapTypeControl: false,
                                    fullscreenControl: true,
                                }}
                            >
                                {locations.map(marker => (
                                    <Marker
                                        position={{ lat: marker.lat, lng: marker.lng }}
                                        key={marker.id}
                                        icon={{
                                            url: marker.carImage,
                                            scaledSize: new window.google.maps.Size(35, 25)
                                        }}
                                    >
                                        <InfoWindow
                                            position={{ lat: marker.lat, lng: marker.lng }}
                                            options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                        >
                                            <div style={{ fontFamily: BRAND.font, fontSize: 12, color: BRAND.slate900, fontWeight: 500 }}>
                                                {marker.drivername}
                                                <br />
                                                <span style={{ color: BRAND.slate500, fontWeight: 400 }}>{marker.carnumber}</span>
                                            </div>
                                        </InfoWindow>
                                    </Marker>
                                ))}
                            </GoogleMap>
                        ) : (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND.slate400, fontSize: 13 }}>
                                Caricamento mappa...
                            </Box>
                        )}
                    </Box>
                </SurfaceCard>

                {/* Sidebar stats */}
                <Box sx={{ display: 'grid', gridTemplateRows: 'repeat(3, 1fr)', gap: { xs: 2, md: 2.5 } }}>
                    <StatTile
                        delay={320}
                        accent={BRAND.teal}
                        accentLight={BRAND.tealLight}
                        icon={<GroupOutlinedIcon sx={{ fontSize: 22, color: BRAND.teal }} />}
                        label={t('total_cumtomer') || 'Strutture totali'}
                        value={customerCount}
                    />
                    <StatTile
                        delay={400}
                        accent={BRAND.indigo}
                        accentLight={BRAND.indigoLight}
                        icon={<LocalShippingOutlinedIcon sx={{ fontSize: 22, color: BRAND.indigo }} />}
                        label={t('total_drivers') || 'Trasportatori totali'}
                        value={driverCount}
                    />
                    <StatTile
                        delay={480}
                        accent={BRAND.green}
                        accentLight={BRAND.greenLight}
                        icon={<VerifiedOutlinedIcon sx={{ fontSize: 22, color: BRAND.green }} />}
                        label={t('active_driver') || 'Trasportatori attivi'}
                        value={activeCount}
                        livePulse
                    />
                </Box>
            </Box>

            {/* CHART */}
            <SurfaceCard delay={560} sx={{ p: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: { xs: 2, md: 3 },
                        py: 2,
                        borderBottom: `1px solid ${BRAND.slate200}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={iconBoxSx(BRAND.indigoLight)}>
                            <BarChartOutlinedIcon sx={{ fontSize: 18, color: BRAND.indigo }} />
                        </Box>
                        <Box>
                            <Typography sx={cardTitleSx}>Andamento prenotazioni {new Date().getFullYear()}</Typography>
                            <Typography sx={cardSubtitleSx}>Totale prenotazioni vs annullate, mese per mese</Typography>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ p: { xs: 1, md: 2 } }}>
                    <Chart
                        options={chartOptions}
                        series={chartSeries}
                        type="area"
                        width="100%"
                        height={360}
                    />
                </Box>
            </SurfaceCard>
        </Box>
    );
};

// ───────────────────────────────── reusable components

function SurfaceCard({ children, delay = 0, sx = {} }) {
    return (
        <Box
            sx={{
                backgroundColor: BRAND.white,
                border: `1px solid ${BRAND.slate200}`,
                borderRadius: '20px',
                boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 16px -4px rgba(15,23,42,0.04)',
                animation: `ml-fade-up 500ms ${delay}ms cubic-bezier(0.16,1,0.3,1) both`,
                opacity: 0,
                fontFamily: BRAND.font,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function KpiCard({ delay, accent, accentLight, icon, label, value, helper }) {
    return (
        <SurfaceCard
            delay={delay}
            sx={{
                p: { xs: 2.5, md: 3 },
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 250ms cubic-bezier(0.4,0,0.2,1)',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -8px rgba(15,23,42,0.08)',
                    borderColor: accent,
                },
            }}
        >
            {/* Decorative corner accent */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -40,
                    right: -40,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    backgroundColor: accentLight,
                    pointerEvents: 'none',
                }}
            />
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={iconBoxSx(accentLight)}>{icon}</Box>
            </Box>
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: BRAND.slate500,
                    fontFamily: BRAND.font,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    mb: 0.5,
                    position: 'relative',
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: { xs: 26, md: 32 },
                    fontWeight: 800,
                    color: BRAND.slate900,
                    fontFamily: BRAND.font,
                    letterSpacing: '-0.025em',
                    lineHeight: 1.1,
                    mb: 0.5,
                    position: 'relative',
                    wordBreak: 'break-word',
                }}
            >
                {value}
            </Typography>
            {helper && (
                <Typography sx={{ fontSize: 12, color: BRAND.slate500, fontFamily: BRAND.font, position: 'relative' }}>
                    {helper}
                </Typography>
            )}
        </SurfaceCard>
    );
}

function StatTile({ delay, accent, accentLight, icon, label, value, livePulse }) {
    return (
        <SurfaceCard
            delay={delay}
            sx={{
                p: { xs: 2, md: 2.5 },
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 250ms cubic-bezier(0.4,0,0.2,1)',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -8px rgba(15,23,42,0.08)',
                    borderColor: accent,
                },
            }}
        >
            <Box sx={{ ...iconBoxSx(accentLight), width: 44, height: 44, borderRadius: '12px', flexShrink: 0 }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: BRAND.slate500, fontFamily: BRAND.font, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: BRAND.slate900, fontFamily: BRAND.font, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                        {value}
                    </Typography>
                    {livePulse && value > 0 && (
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: BRAND.green,
                                animation: 'ml-pulse 2s ease-in-out infinite',
                            }}
                        />
                    )}
                </Box>
            </Box>
        </SurfaceCard>
    );
}

const sectionLabelSx = {
    fontSize: 11,
    fontWeight: 700,
    color: BRAND.slate500,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontFamily: BRAND.font,
};

const cardTitleSx = {
    fontSize: { xs: 14, md: 15 },
    fontWeight: 700,
    color: BRAND.slate900,
    fontFamily: BRAND.font,
    letterSpacing: '-0.015em',
    lineHeight: 1.2,
};

const cardSubtitleSx = {
    fontSize: 12,
    color: BRAND.slate500,
    fontFamily: BRAND.font,
    mt: 0.25,
};

const iconBoxSx = (bgColor) => ({
    width: 36,
    height: 36,
    borderRadius: '10px',
    backgroundColor: bgColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
});

const KEYFRAMES_CSS = `
@keyframes ml-fade-up {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes ml-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.15); }
}
@media (prefers-reduced-motion: reduce) {
  [style*="ml-fade-up"], [style*="ml-pulse"] { animation: none !important; opacity: 1 !important; transform: none !important; }
}
`;

export default Dashboard;
